'use no memo';

import type { MeetingAttendanceStatus, MeetingPublic } from '@repo/types/meetings';
import type { UserPublic } from '@repo/types/users';
import type { ColumnDef, Table as TableInstance } from '@tanstack/react-table';

import { cn } from '@repo/lib/cn';
import { flexRender } from '@tanstack/react-table';
import { format, parseISO } from 'date-fns';
import { Dot } from 'lucide-react';
import { useMemo } from 'react';
import { Link } from 'react-router';

import { TanstackTableHeaderContent } from '@/components/features/manage/tanstack-table/TanstackTable';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHeader, TableRow } from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { getAvatar } from '@/utils/getAvatar';

// Local meta type for this component only
type AttendanceColumnMeta = {
  cellClassName?: string;
  headerClassName?: string;
};

export interface AttendanceRow {
  user: UserPublic;
  statusByMeetingId: Record<number, MeetingAttendanceStatus>;
  totals: Record<MeetingAttendanceStatus, number>;
}

export const STATUS_CONFIG: Record<
  MeetingAttendanceStatus,
  {
    label: string;
    summaryLabel: string;
    dotClassName: string;
    cellClassName: string;
  }
> = {
  present: {
    label: 'Obecny',
    summaryLabel: 'Obecności',
    dotClassName: 'text-success',
    cellClassName: 'bg-success-accent text-success border-border-success',
  },
  absent: {
    label: 'Nieobecny',
    summaryLabel: 'Nieobecności',
    dotClassName: 'text-destructive',
    cellClassName: 'bg-destructive-accent text-destructive border-border-destructive',
  },
  late: {
    label: 'Spóźnienie',
    summaryLabel: 'Spóźnienia',
    dotClassName: 'text-warning',
    cellClassName: 'bg-warning-accent text-warning border-border-warning',
  },
};

export const STATUS_ORDER: MeetingAttendanceStatus[] = ['present', 'absent', 'late'];

function getUserInitials(user: UserPublic): string {
  const first = user.name.trim().charAt(0);
  const last = user.surname.trim().charAt(0);
  const initials = `${first}${last}`.trim();
  return initials.length > 0 ? initials.toUpperCase() : '—';
}

export function useAttendanceColumns(meetings: MeetingPublic[]): ColumnDef<AttendanceRow>[] {
  return useMemo<ColumnDef<AttendanceRow>[]>(() => {
    const studentColumn: ColumnDef<AttendanceRow, unknown> = {
      id: 'student',
      header: 'Uczeń',
      accessorFn: (row) => `${row.user.name} ${row.user.surname} ${row.user.email}`,
      enableSorting: true,
      enableGlobalFilter: true,
      cell: ({ row }) => {
        const { user } = row.original;
        return (
          <div className="flex items-center gap-2">
            <Avatar size="sm">
              <AvatarImage src={getAvatar(user.avatarURL)} alt={`avatar-${user.name}`} />
              <AvatarFallback>{getUserInitials(user)}</AvatarFallback>
            </Avatar>
            <span className="text-foreground truncate text-sm font-medium">
              {user.name} {user.surname}
            </span>
          </div>
        );
      },
      meta: {
        headerClassName: 'min-w-72',
        cellClassName: 'min-w-72',
      } as AttendanceColumnMeta,
    };

    const summaryColumn: ColumnDef<AttendanceRow, unknown> = {
      id: 'summary',
      header: 'Frekwencja',
      accessorFn: (row) => row.totals.present,
      enableSorting: true,
      enableGlobalFilter: false,
      cell: ({ row }) => (
        <div className="flex flex-wrap gap-1">
          {STATUS_ORDER.map((status) => {
            const config = STATUS_CONFIG[status];
            const total = row.original.totals[status];
            return (
              <Tooltip key={status}>
                <TooltipTrigger asChild>
                  <Badge size="sm" variant="muted" border className="text-foreground gap-1">
                    <Dot className={cn('size-3 stroke-[10px]', config.dotClassName)} />
                    {config.label}: {total}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>
                  {config.summaryLabel}: {total}
                </TooltipContent>
              </Tooltip>
            );
          })}
        </div>
      ),
      meta: {
        headerClassName: 'min-w-96 border-border-primary border-l',
        cellClassName: 'min-w-96 border-border-primary border-l',
      } as AttendanceColumnMeta,
    };

    const meetingColumns: ColumnDef<AttendanceRow, unknown>[] = meetings.map((meeting) => ({
      id: `meeting-${meeting.id}`,
      header: () => (
        <Link to={`/calendar/meeting/${meeting.id}`} className="group flex flex-col items-center gap-0.5 text-xs font-medium">
          <span className="group-hover:underline">{format(parseISO(meeting.startDate), 'd MMM, yyyy')}</span>
          <span className="text-muted-foreground">{meeting.name}</span>
        </Link>
      ),
      accessorFn: (row) => row.statusByMeetingId[meeting.id] ?? '',
      enableSorting: false,
      enableGlobalFilter: false,
      cell: ({ row }) => {
        const status = row.original.statusByMeetingId[meeting.id];
        const config = status ? STATUS_CONFIG[status] : null;
        return (
          <div className={cn('flex h-12 items-center justify-center border-l-4 px-2 text-sm font-medium', config ? config.cellClassName : 'bg-background text-muted-foreground border-border-primary')}>
            {config ? config.label : '—'}
          </div>
        );
      },
      meta: {
        headerClassName: 'min-w-24 border-border-primary border-l text-center',
        cellClassName: 'min-w-24 border-border-primary border-l p-0',
      } as AttendanceColumnMeta,
    }));

    return [studentColumn, summaryColumn, ...meetingColumns];
  }, [meetings]);
}

export function AttendanceTableSkeleton() {
  return (
    <div className="border-border-primary overflow-hidden rounded-md border">
      <div className="w-full overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-input border-border-primary">
              <TableCell className="min-w-72 px-3 py-2">
                <Skeleton className="h-4 w-16" />
              </TableCell>
              <TableCell className="border-border-primary min-w-96 border-l px-3 py-2">
                <Skeleton className="h-4 w-24" />
              </TableCell>
              {Array.from({ length: 3 }).map((_, i) => (
                <TableCell key={i} className="border-border-primary min-w-24 border-l px-3 py-2">
                  <div className="flex flex-col items-center gap-1">
                    <Skeleton className="h-3 w-16" />
                    <Skeleton className="h-3 w-12" />
                  </div>
                </TableCell>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {Array.from({ length: 6 }).map((_, rowIdx) => (
              <TableRow key={rowIdx} className="border-border-primary">
                <TableCell className="min-w-72 px-3 py-2">
                  <div className="flex items-center gap-2">
                    <Skeleton className="size-8 rounded-full" />
                    <Skeleton className="h-4 w-32" />
                  </div>
                </TableCell>
                <TableCell className="border-border-primary min-w-96 border-l px-3 py-2">
                  <div className="flex gap-1">
                    <Skeleton className="h-5 w-20 rounded-full" />
                    <Skeleton className="h-5 w-24 rounded-full" />
                    <Skeleton className="h-5 w-20 rounded-full" />
                  </div>
                </TableCell>
                {Array.from({ length: 3 }).map((_, colIdx) => (
                  <TableCell key={colIdx} className="border-border-primary min-w-24 border-l p-0">
                    <div className="flex h-12 items-center justify-center px-2">
                      <Skeleton className="h-4 w-14" />
                    </div>
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

interface AttendanceTableProps {
  table: TableInstance<AttendanceRow>;
}

export default function AttendanceTable(attendanceTableProps: AttendanceTableProps) {
  const { table } = attendanceTableProps;
  const totalColumns = table.getAllColumns().length;
  const rows = table.getRowModel().rows;

  return (
    <div className="border-border-primary overflow-hidden rounded-md border">
      <div className="w-full overflow-x-auto">
        <Table>
          <TableHeader>
            <TanstackTableHeaderContent table={table} rowClassName="bg-input border-border-primary" />
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={totalColumns} className="text-muted-foreground py-6 text-center">
                  Brak danych do wyświetlenia.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row.id} className="border-border-primary">
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} className={cn((cell.column.columnDef.meta as AttendanceColumnMeta)?.cellClassName)}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
